import React, {
  forwardRef, useRef, useEffect, useState, useImperativeHandle,
} from 'react';
import PropTypes from 'prop-types';

// Utils
import { Calendar } from 'react-date-range';
import { fr } from 'date-fns/locale';
import { format } from 'date-fns';
import InputField from '../InputField/InputField';
import cn from '../../../utils/tools/cn';

import styles from './DatePickerInput.module.scss';

const DatePickerInput = forwardRef(({
  id, label, date, setDate, disabled, dateDisabled, error, minDate, maxDate, onAfterClose, showTopLabel,
}, ref) => {
  const calendarRef = useRef();
  const datePickerRef = useRef();

  const [calendarIsOpened, setCalendarIsOpened] = useState(false);

  useImperativeHandle(
    ref,
    () => ({
      handleOpenCalendar: () => {
        setCalendarIsOpened(true);
      },
    }),
  );

  const handleClickOutside = (e) => {
    if (calendarRef?.current && !calendarRef.current.contains(e.target)) {
      setCalendarIsOpened(false);
    }
  };

  const handleUnlockPointer = () => {
    const calendarElement = document.querySelector(`#${id}`);
    if (calendarElement) calendarElement.style.pointerEvents = 'initial';
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mouseup', handleUnlockPointer);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mouseup', handleUnlockPointer);
    };
  }, []);

  useEffect(() => {
    if (calendarRef?.current) {
      const holder = document.querySelector('#datePicker_holder');
      if (holder) {
        const existant = holder.querySelector(`#${id}`);
        if (existant) existant.remove();
        holder.appendChild(calendarRef.current);
      }
    }
  }, [calendarRef]);

  useEffect(() => {
    if (calendarIsOpened) {
      const calendarElement = document.querySelector(`#${id}`);

      if (calendarElement && datePickerRef?.current) {
        let top = datePickerRef.current.getBoundingClientRect().top + datePickerRef.current.offsetHeight;
        const { left } = datePickerRef.current.getBoundingClientRect();
        const { height } = calendarElement.getBoundingClientRect();

        const outOfScreen = window.innerHeight - (top + height + 20);
        if (outOfScreen < 0) top += outOfScreen;

        calendarElement.style.top = `${top}px`;
        calendarElement.style.left = `${left}px`;
        calendarElement.style.pointerEvents = 'none'; // lock pointer to prevent mistaken clicks
      }
    }
  }, [calendarIsOpened]);

  return (
    <div ref={datePickerRef} className={styles.wrapper}>
      <InputField
        value={date ? format(date, 'dd/MM/yyyy') : ''}
        label={label}
        onFocus={() => {
          if (!disabled) setCalendarIsOpened(true);
        }}
        disabled={disabled}
        error={error}
      />

      <div
        id={id}
        ref={calendarRef}
        className={cn([
          styles.calendar,
          calendarIsOpened ? styles.opened : '',
        ])}
      >
        {showTopLabel ? (
          <p className="fs-14 fw-500 c-primary-500 p-10">
            {label}
          </p>
        ) : null}
        <Calendar
          date={date}
          onChange={(value) => {
            setDate(value);
            setCalendarIsOpened(false);
            onAfterClose();
          }}
          weekStartsOn={1}
          monthDisplayFormat="LLLL yyyy"
          showDateDisplay={false}
          minDate={minDate || new Date()}
          maxDate={maxDate || undefined}
          showMonthAndYearPickers={false}
          weekdayDisplayFormat="EEEEEE"
          locale={fr}
          preventSnapRefocus
          disabledDay={(d) => dateDisabled(d)}
          fixedHeight
        />
      </div>
    </div>
  );
});

DatePickerInput.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  date: PropTypes.shape({}),
  setDate: PropTypes.func,
  disabled: PropTypes.bool,
  dateDisabled: PropTypes.func,
  error: PropTypes.string,
  minDate: PropTypes.shape({}),
  maxDate: PropTypes.shape({}),
  onAfterClose: PropTypes.func,
  showTopLabel: PropTypes.bool,
};

DatePickerInput.defaultProps = {
  label: null,
  date: null,
  setDate: () => {},
  disabled: false,
  dateDisabled: () => {},
  error: null,
  minDate: null,
  maxDate: null,
  onAfterClose: () => {},
  showTopLabel: false,
};

export default DatePickerInput;
